import { DoseChangeHolds } from "./DoseChangeHolds";
import { CareTeamNotes } from "./CareTeamNotes";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Eye,
  FileCheck2,
  FileImage,
  FileText,
  FolderOpen,
  MessageSquare,
  Send,
  Sparkles,
  Stethoscope,
  UserRound,
  Video,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  MessageDocumentCard,
  RichMessageContent,
} from "@/components/content/RichMessageContent";
import { Badge, Button, Drawer } from "@/design-system/primitives";
import {
  useClinicianTemplates,
  useClinicianGuides,
  useClinicianSession,
  useCompleteClinicianTask,
  useJoinClinicianAppointment,
  useDraftOutcome,
  usePatientConversationDraft,
  useSendPatientMessage,
  useClinicianCheckinDoseHistory,
  useWorklistPatientBrief,
  worklistPatientBriefQuery,
  useWithdrawClinicianVisit,
} from "@/lib/api/clinicianHooks";
import type {
  IntakeFormPreview,
  PatientBriefResponse,
  PrescriptionStage,
  PrescriptionWorkItem,
} from "@/types/clinician";
import { clinicianMessageError } from "./messageError";
import { clinicianWorklistDestination } from "./clinicianNavigation";
import { prescriptionMedicationContext } from "./medicationContext";
import {
  DocumentPreview,
  IdentityDocumentPreview,
} from "./PatientDocumentPreview";
import {
  PrescriptionDecisionModal,
  type PrescriptionDecision,
} from "./PrescriptionDecisionModal";
import { VisitOverrideModal } from "./VisitOverrideModal";
import { VisitRequestModal } from "./VisitRequestModal";
import { CheckinDoseStrip } from "./CheckinDoseHistory";
import { PatientChartDrawer, type ChartView } from "./ConversationScreen";
import { findIntakeOutliers, intakeBmi, type BmiReading } from "./intakeOutliers";
import { MasterProductEvidence } from "./MasterProductEvidence";
import { IntakeAnswerTable } from "./IntakeAnswerTable";
import {
  highlightsForForm,
  IntakeHighlights,
  intakeSubmissionDate,
} from "./IntakeHighlights";
import { appendLearningLink, LearningLinkPicker } from "./LearningLinkPicker";
import { GuideAttachmentPicker } from "./GuideAttachmentPicker";
import {
  assignmentIssueLabel,
  PrescriberAssignment,
} from "./PrescriberAssignment";

const STAGE: Record<
  PrescriptionStage,
  {
    label: string;
    detail: string;
    tone: "success" | "warning" | "danger" | "info";
  }
> = {
  awaiting_id: {
    label: 'Missing ID',
    detail: 'An ID must be on the patient’s profile before treatment review. Verify the patient’s identity during prescribing. Scheduled visits remain available.',
    tone: 'warning',
  },
  ready_to_prescribe: {
    label: "Ready to prescribe",
    detail: "Product intake and visit gates are satisfied.",
    tone: "success",
  },
  approval_required: {
    label: "Approval required",
    detail: "A physician decision is required before this order can advance.",
    tone: "danger",
  },
  intake_required: {
    label: "Intake required",
    detail: "The product-linked intake is missing or must be renewed.",
    tone: "warning",
  },
  sync_required: {
    label: "Sync visit required",
    detail: "A live clinician visit must occur before prescribing.",
    tone: "warning",
  },
  visit_scheduled: {
    label: "Visit scheduled",
    detail:
      "Start the scheduled provider video visit; prescribing unlocks when the meeting begins.",
    tone: "info",
  },
  prescribed: {
    label: "Prescribed",
    detail: "The medication record was written and remains visible as history.",
    tone: "success",
  },
  rejected: {
    label: "Rejected / follow-up",
    detail:
      "The clinical rejection is recorded; this item remains visible for follow-up.",
    tone: "danger",
  },
  missing_info: {
    label: "Missing information",
    detail:
      "Support or operations must resolve the requested information before clinical review continues.",
    tone: "warning",
  },
};

function formatRedoDate(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function openVisitRequest(item: PrescriptionWorkItem) {
  const request = item.visit_request;
  return request && request.resolved_at == null ? request : null;
}

/** Row tag for a consult gate the clinician raised rather than the state. */
export function clinicianVisitTag(item: PrescriptionWorkItem) {
  return item.consult_reason === "clinician_request" &&
    (item.stage === "sync_required" || item.stage === "visit_scheduled")
    ? "Requested by clinician"
    : null;
}

function stageFor(item: PrescriptionWorkItem) {
  if (item.submission_followup) {
    return {
      label: "Approved · ops follow-up",
      detail: "Approval is saved. Operations is reconciling pharmacy fulfillment in the existing support ticket.",
      tone: "warning" as const,
    };
  }
  if (
    item.stage === "intake_required" &&
    item.intake_kind === "renewal_checkin"
  ) {
    return {
      label: "Monthly symptom check-in missing",
      detail:
        "The renewal is paid and visible here; prescribing unlocks after the patient completes this month's symptom check-in.",
      tone: "warning" as const,
    };
  }
  if (
    item.stage === "sync_required" &&
    item.consult_reason === "clinician_request"
  ) {
    return {
      label: "Visit requested",
      detail:
        "You asked the patient to book a visit before prescribing. Prescribing unlocks when the visit starts or you record attendance.",
      tone: "info" as const,
    };
  }
  return STAGE[item.stage];
}

function patientName(item: PrescriptionWorkItem) {
  return item.patient.display_name || item.patient.email || "Unknown patient";
}

function waitLabel(hours: number) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function PrescribingWorkspace({
  items,
}: {
  items: PrescriptionWorkItem[];
}) {
  const { prescriptionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const selected = items.find((item) => item.id === prescriptionId) ?? null;
  const closePatientChart = () => {
    navigate(clinicianWorklistDestination(location.search), { replace: true });
  };

  useEffect(() => {
    if (!selected) return;
    const index = items.findIndex((item) => item.id === selected.id);
    for (const neighbor of [items[index - 1], items[index + 1]]) {
      if (!neighbor) continue;
      void queryClient.prefetchQuery(
        worklistPatientBriefQuery(neighbor.patient.id, neighbor.id),
      );
    }
  }, [items, queryClient, selected]);

  return (
    <div className="border border-line-subtle bg-raised min-h-[42rem] grid xl:grid-cols-[22rem_minmax(0,1fr)]">
      <aside className="border-b xl:border-b-0 xl:border-r border-line-subtle max-h-[34rem] xl:max-h-[calc(100vh-12rem)] overflow-y-auto">
        <div className="sticky top-0 z-[2] bg-sunken border-b border-line-subtle px-10 py-8">
          <span className="type-label-xs text-ink-muted">
            PATIENT QUEUE · {items.length}
          </span>
        </div>
        <div className="divide-y divide-line-subtle">
          {items.map((item) => {
            const stage = stageFor(item);
            const active = selected?.id === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  navigate({
                    pathname: `/clinician-portal/today/${item.id}`,
                    search: location.search,
                  })
                }
                className={`w-full text-left p-11 flex gap-8 items-start focus-visible:outline-none focus-visible:shadow-focus ${
                  active ? "bg-ink text-page" : "hover:bg-sunken"
                }`}
              >
                <div className="min-w-0 flex-1 flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-8">
                    <span className="type-body-sm font-medium truncate">
                      {patientName(item)}
                    </span>
                    <span
                      className={`text-10 tabular-nums shrink-0 ${active ? "text-page/60" : "text-ink-muted"}`}
                    >
                      {item.submission_followup
                        ? "OPS FOLLOW-UP"
                        : item.stage === "prescribed"
                        ? "DONE"
                        : waitLabel(item.waiting_hours)}
                    </span>
                  </div>
                  <span
                    className={`text-11 truncate ${active ? "text-page/70" : "text-ink-muted"}`}
                  >
                    {item.product}
                  </span>
                  {item.supply_summary && (
                    <span className="text-11 font-medium break-words">
                      {item.supply_summary}
                    </span>
                  )}
                  {item.assigned_clinician && (
                    <span
                      className={`text-10 truncate ${active ? "text-page/60" : "text-ink-muted"}`}
                    >
                      Owner ·{" "}
                      {item.assigned_clinician.display_name ||
                        item.assigned_clinician.email}
                    </span>
                  )}
                  {(item.patient_state || assignmentIssueLabel(item)) && (
                    <span className="flex flex-wrap items-center gap-4">
                      {item.patient_state && (
                        <span
                          className={`px-5 py-1 type-label-xs border ${active ? "border-page/40 text-page/80" : "border-line bg-sunken text-ink-muted"}`}
                        >
                          {item.patient_state}
                        </span>
                      )}
                      {assignmentIssueLabel(item) && (
                        <span
                          className={`px-5 py-1 type-label-xs border ${active ? "border-danger/50 bg-danger text-page" : "border-danger/30 bg-danger-wash text-danger"}`}
                        >
                          {assignmentIssueLabel(item)}
                        </span>
                      )}
                    </span>
                  )}
                  <span
                    className={`type-label-xs ${active ? "text-page/70" : stage.tone === "danger" ? "text-danger" : "text-ink-muted"}`}
                  >
                    {stage.label}
                  </span>
                  {(item.support_escalations ?? []).length > 0 && (
                    <span
                      className={`self-start px-5 py-2 type-label-xs border ${active ? "border-warning/50 bg-warning text-ink" : "border-warning/30 bg-warning-wash text-warning"}`}
                    >
                      Support escalation
                    </span>
                  )}
                  {clinicianVisitTag(item) && (
                    <span
                      className={`self-start px-5 py-2 type-label-xs border ${active ? "border-page/40 text-page/80" : "border-line bg-sunken text-ink-muted"}`}
                    >
                      {clinicianVisitTag(item)}
                    </span>
                  )}
                </div>
                <ChevronRight
                  size={13}
                  className="mt-2 shrink-0 opacity-50"
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </aside>

      {selected ? (
        <PatientWorkspace
          key={selected.id}
          item={selected}
          onClose={closePatientChart}
        />
      ) : (
        <div className="min-h-[30rem] p-30 flex flex-col items-center justify-center text-center gap-8">
          <Stethoscope size={22} className="text-ink-muted" aria-hidden />
          <p className="type-heading-sm">Select a patient to begin review</p>
          <p className="type-body-sm text-ink-muted max-w-[34rem]">
            Their product intake, chart facts, messages, appointments, and
            prescribing actions will appear here.
          </p>
        </div>
      )}
    </div>
  );
}

function PatientWorkspace({
  item,
  onClose,
}: {
  item: PrescriptionWorkItem;
  onClose: () => void;
}) {
  const brief = useWorklistPatientBrief(item.patient.id, item.id);
  const checkins = useClinicianCheckinDoseHistory(item.patient.id);
  const [chartView, setChartView] = useState<ChartView | null>(null);
  const stage = stageFor(item);
  // The history groups program tiers under the program SKU the order carries;
  // fall back to the family of the row this order produced.
  const checkinFamily =
    checkins.data?.medications.find((card) => card.family === item.product_id)
      ?.family ??
    checkins.data?.history.find((row) => row.order_number === item.order_number)
      ?.family ??
    item.product_id ??
    "";
  return (
    <main className="min-w-0">
      <header className="p-14 md:p-18 border-b border-line-subtle flex flex-wrap items-start justify-between gap-10">
        <div className="min-w-0 flex flex-col gap-4">
          <span className="type-label-xs text-ink-muted">
            {[item.order_number, item.channel].filter(Boolean).join(" · ")}
          </span>
          <h2 className="type-heading-lg truncate">{patientName(item)}</h2>
          {brief.data && <IdentityDocumentPreview data={brief.data} />}
          <p className="type-body-sm text-ink-muted break-words">
            {item.product}
          </p>
          <PrescriptionSupply summary={item.supply_summary} />
          <PrescriberAssignment
            patientId={item.patient.id}
            assigned={item.assigned_clinician}
            patientState={item.patient_state}
            assignmentIssue={item.assignment_issue}
          />
        </div>
        <div className="flex flex-col items-end gap-4">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close patient chart"
          >
            <X size={14} aria-hidden />
            Close chart
          </Button>
          <div className="flex flex-wrap items-center justify-end gap-5">
            {clinicianVisitTag(item) && (
              <Badge tone="neutral" size="sm">
                {clinicianVisitTag(item)}
              </Badge>
            )}
            <Badge tone={stage.tone} size="sm">
              {stage.label}
            </Badge>
          </div>
          <span className="text-11 text-ink-muted max-w-[24rem] text-right">
            {stage.detail}
          </span>
        </div>
      </header>

      {brief.isLoading ? (
        <div className="p-20 grid lg:grid-cols-2 gap-12 animate-pulse">
          <div className="h-[30rem] bg-sunken" />
          <div className="h-[30rem] bg-sunken" />
        </div>
      ) : brief.isError || !brief.data ? (
        <div className="p-30 text-center type-body-sm text-danger">
          Patient clinical context could not be loaded. Nothing was changed.
        </div>
      ) : (
        <div className="p-12 md:p-18 grid 2xl:grid-cols-[minmax(0,1.45fr)_minmax(22rem,0.8fr)] gap-14 items-start">
          <div className="min-w-0 flex flex-col gap-14">
            <CareTeamNotes patientId={item.patient.id} />
            <SupportEscalations item={item} />
            <DoseChangeHolds patientId={item.patient.id} orderId={item.id} prescriber={item.assigned_clinician} />
            <MissingInfoResolution item={item} />
            <MasterProductEvidence
              patientId={brief.data.profile.id}
              productId={item.product_id}
              orderId={item.id}
              legacyFallback={
                <ProductIntake data={brief.data} productId={item.product_id} />
              }
            />
            {checkins.data && (
              <CheckinDoseStrip
                data={checkins.data}
                family={checkinFamily}
                onOpenHistory={() => setChartView("checkins")}
              />
            )}
            <PrescribingActions
              item={item}
              data={brief.data}
              onClosePatient={onClose}
            />
          </div>
          <div className="min-w-0 flex flex-col gap-12">
            <PatientFacts data={brief.data} item={item} />
            <AppointmentCard data={brief.data} patientId={item.patient.id} />
            <RecentMessages item={item} data={brief.data} />
            <PatientDocuments data={brief.data} />
          </div>
          <PatientChartDrawer
            open={chartView !== null}
            onClose={() => setChartView(null)}
            data={brief.data}
            view={chartView ?? "checkins"}
            onViewChange={setChartView}
          />
        </div>
      )}
    </main>
  );
}

function SupportEscalations({ item }: { item: PrescriptionWorkItem }) {
  const complete = useCompleteClinicianTask();
  const [message, setMessage] = useState<string | null>(null);
  if (!(item.support_escalations ?? []).length) return null;
  return (
    <section className="border border-warning/40 bg-warning-wash">
      <div className="px-11 py-9 border-b border-warning/30 flex items-center gap-7">
        <AlertTriangle size={15} className="text-warning" aria-hidden />
        <div>
          <h3 className="type-label-sm font-medium text-warning">
            Support escalation
          </h3>
          <p className="text-11 text-ink-muted">
            Support needs a clinician decision for this patient.
          </p>
        </div>
      </div>
      <div className="divide-y divide-warning/20">
        {(item.support_escalations ?? []).map((escalation) => (
          <article
            key={escalation.ticket_id}
            className="p-11 flex flex-col gap-6"
          >
            <div className="flex flex-wrap items-start justify-between gap-7">
              <div className="min-w-0">
                <p className="type-label-xs text-ink-muted">
                  {escalation.ticket_number}
                </p>
                <p className="type-body-sm font-medium mt-2">
                  {escalation.title}
                </p>
              </div>
              {escalation.escalated_at && (
                <time className="text-11 text-ink-muted">
                  {new Date(escalation.escalated_at).toLocaleString()}
                </time>
              )}
            </div>
            {escalation.body && (
              <p className="type-body-sm whitespace-pre-wrap">
                {escalation.body}
              </p>
            )}
            {escalation.note && (
              <div className="border-l-2 border-warning pl-8">
                <p className="type-label-xs text-ink-muted">
                  NOTE FROM SUPPORT
                </p>
                <p className="type-body-sm mt-2 whitespace-pre-wrap">
                  {escalation.note}
                </p>
              </div>
            )}
            {escalation.clinician_task_id && (
              <div className="flex items-center justify-between gap-8">
                <span className="type-body-sm text-success">{message}</span>
                <Button
                  type="button"
                  size="sm"
                  loading={complete.isPending}
                  onClick={async () => {
                    setMessage(null);
                    try {
                      await complete.mutateAsync(escalation.clinician_task_id!);
                      setMessage("Returned to support.");
                    } catch {
                      setMessage(
                        "Could not return this escalation to support.",
                      );
                    }
                  }}
                >
                  Mark reviewed & return to support
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function MissingInfoResolution({ item }: { item: PrescriptionWorkItem }) {
  const request = item.missing_info;
  if (!request?.resolved_at) return null;
  return (
    <section className="border border-success/30 bg-success-wash p-11 flex gap-8">
      <CheckCircle2 size={16} className="text-success shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="type-label-sm text-success">
          Missing information resolved
        </p>
        <p className="type-body-sm mt-3">
          {request.reason_label || "Support follow-up"}
        </p>
        {request.resolution_note && (
          <p className="type-body-sm mt-4 whitespace-pre-wrap">
            {request.resolution_note}
          </p>
        )}
        <p className="text-11 text-ink-muted mt-4">
          {[request.resolved_by, new Date(request.resolved_at).toLocaleString()]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
    </section>
  );
}

const BMI_FLAGS = {
  below_25: {
    note: "Below 25",
    box: "border-danger/30 bg-danger-wash",
    text: "text-danger",
  },
  "25_to_27": {
    note: "25–27",
    box: "border-warning/30 bg-warning-wash",
    text: "text-ink",
  },
} as const;

function BmiCard({ reading }: { reading: BmiReading }) {
  const flag = reading.flag ? BMI_FLAGS[reading.flag] : null;
  return (
    <div
      role={flag ? "status" : undefined}
      className={`p-8 border mb-7 ${flag?.box ?? "border-line-subtle bg-sunken"}`}
    >
      <p className="text-11 text-ink-muted mb-5">
        Calculated from {Math.round(reading.heightIn)} in ·{" "}
        {Math.round(reading.weightLb)} lb
        {reading.heightForm &&
          ` · height from ${reading.heightForm.form_name}, ${intakeSubmissionDate(reading.heightForm.submitted_at)}`}
      </p>
      <span className={`type-label-xs ${flag?.text ?? "text-ink-muted"}`}>
        BMI
      </span>
      <p className={`type-body-sm font-medium mt-3 ${flag?.text ?? ""}`}>
        {reading.value.toFixed(1)}
        {flag && ` · ${flag.note}`}
      </p>
    </div>
  );
}

export function ProductIntake({
  data,
  productId,
}: {
  data: PatientBriefResponse;
  productId?: string | null;
}) {
  const relevant = data.intake_forms.filter((form) =>
    data.relevant_intake_form_ids.includes(form.form_id ?? ""),
  );
  const preferredId = (relevant[0] ?? data.intake_forms[0])?.id ?? "";
  const [activeId, setActiveId] = useState(preferredId);
  useEffect(() => setActiveId(preferredId), [preferredId]);
  const active =
    data.intake_forms.find((form) => form.id === activeId) ??
    relevant[0] ??
    data.intake_forms[0];
  const otherForms = data.intake_forms.filter(
    (form) => !data.relevant_intake_form_ids.includes(form.form_id ?? ""),
  );
  const highlights = highlightsForForm(active);
  const selectedForms = active ? [active] : [];
  const outliers = findIntakeOutliers(selectedForms);
  const doseAnswers = selectedForms
    .flatMap((form) => form.answers)
    .filter((answer) => {
      const question = answer.question.toLowerCase();
      return (
        question.includes("current dose") ||
        question.includes("present dose") ||
        question.includes("dose amount") ||
        question.includes("weekly dose")
      );
    });
  const sourcePrescriptions = data.documents
    .filter((document) => document.purpose === "transfer_prescription")
    .sort(
      (left, right) =>
        Date.parse(right.uploaded_at) - Date.parse(left.uploaded_at),
    );
  const isTirzepatide = String(productId ?? "")
    .toLowerCase()
    .startsWith("tirzepatide-");
  const isGlp = /^(?:tirzepatide|semaglutide)-/i.test(String(productId ?? ""));
  const bmi = isGlp && active ? intakeBmi(active, data.intake_forms) : null;
  return (
    <section className="border border-line-subtle">
      <div className="p-11 bg-sunken border-b border-line-subtle flex flex-wrap items-center justify-between gap-8">
        <div className="flex items-center gap-6">
          <FileText size={14} aria-hidden />
          <div>
            <h3 className="type-label-sm font-medium">Product intake</h3>
            <p className="text-11 text-ink-muted">
              The intake required for this purchase is shown first.
            </p>
          </div>
        </div>
        {data.intake_forms.length > 1 && (
          <select
            aria-label="Intake submission"
            value={activeId}
            onChange={(event) => setActiveId(event.target.value)}
            className="min-h-34 max-w-[24rem] border border-line bg-raised px-8 type-label-xs"
          >
            {data.intake_forms.map((form) => (
              <option key={form.id} value={form.id}>
                {data.relevant_intake_form_ids.includes(form.form_id ?? "")
                  ? `Required · ${form.form_name} · `
                  : `${form.form_name} · `}
                {intakeSubmissionDate(form.submitted_at)}
              </option>
            ))}
          </select>
        )}
      </div>

      {active && (
        <section
          aria-label="Clinical highlights"
          className="p-11 border-b border-line-subtle"
        >
          <p className="type-label-xs text-ink-muted mb-7">
            CLINICAL HIGHLIGHTS
          </p>
          <p className="text-11 text-ink-muted mb-7">
            Selected submission: {active.form_name} ·{" "}
            {intakeSubmissionDate(active.submitted_at)}. Highlights and answers
            below refer to this submission only. Earlier submissions remain
            available in the intake selector.
          </p>
          {bmi && <BmiCard reading={bmi} />}
          {highlights.length ? (
            <IntakeHighlights entries={highlights} />
          ) : (
            <p className="type-body-sm text-ink-muted">
              No Healthie clinical highlights for this submission. Review the
              detailed answers below.
            </p>
          )}
        </section>
      )}

      {outliers.length > 0 && (
        <div className="p-11 border-b border-danger/20 bg-danger-wash">
          <p className="type-label-xs text-danger mb-7">
            PROGRAMMATIC INTAKE CHECKS · VERIFY BEFORE PRESCRIBING
          </p>
          <div className="grid md:grid-cols-2 gap-7">
            {outliers.map((outlier) => (
              <div key={outlier.code} className="border border-danger/30 p-8">
                <p className="type-label-sm text-danger">{outlier.label}</p>
                <p className="type-body-sm mt-3">{outlier.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {isTirzepatide &&
        (doseAnswers.length > 0 || sourcePrescriptions.length > 0) && (
          <div className="p-11 border-b border-line-subtle">
            <p className="type-label-xs text-ink-muted mb-7">
              TRANSFER DOSE VERIFICATION
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-line-subtle bg-sunken p-9">
                <p className="type-label-xs text-ink-muted">
                  PATIENT-REPORTED DOSE
                </p>
                {doseAnswers.length ? (
                  doseAnswers.map((answer, index) => (
                    <div key={`${answer.question}-${index}`} className="mt-5">
                      <p className="text-11 text-ink-muted">
                        {answer.question}
                      </p>
                      <p className="type-body-sm font-medium">
                        {answer.answer}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="type-body-sm text-ink-muted mt-4">
                    No transfer dose was identified in this intake.
                  </p>
                )}
              </div>
              <div className="border border-line-subtle p-9">
                <p className="type-label-xs text-ink-muted">
                  SOURCE PRESCRIPTION / VIAL
                </p>
                {sourcePrescriptions.length ? (
                  <div className="mt-6 flex flex-col gap-6">
                    {sourcePrescriptions.slice(0, 2).map((document) => (
                      <a
                        key={document.id}
                        href={document.download_url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-line-subtle p-6 hover:bg-sunken"
                      >
                        {document.mime.startsWith("image/") &&
                        document.download_url ? (
                          <img
                            src={document.download_url}
                            alt={document.filename}
                            className="max-h-[12rem] w-full object-contain"
                          />
                        ) : (
                          <span className="flex items-center gap-6 type-body-sm">
                            <FileText size={14} aria-hidden />{" "}
                            {document.filename}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="type-body-sm text-ink-muted mt-4">
                    No source prescription image or PDF is on file. Treat the
                    listed dose as patient-reported only.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

      {active ? (
        <IntakeAnswers form={active} />
      ) : (
        <div className="p-18 type-body-sm text-warning">
          No completed product intake was found for this patient.
        </div>
      )}

      {otherForms.length > 0 && (
        <details className="border-t border-line-subtle">
          <summary className="cursor-pointer px-11 py-9 type-label-sm text-ink-muted hover:text-ink">
            Other patient intakes ({otherForms.length})
          </summary>
          <div className="px-11 pb-11 grid sm:grid-cols-2 gap-6">
            {otherForms.map((form) => (
              <button
                key={form.id}
                type="button"
                onClick={() => setActiveId(form.id)}
                className="text-left border border-line-subtle p-8 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
              >
                <span className="type-body-sm font-medium block truncate">
                  {form.form_name}
                </span>
                <span className="text-11 text-ink-muted">
                  {form.submitted_at
                    ? new Date(form.submitted_at).toLocaleDateString()
                    : "Date unavailable"}
                </span>
              </button>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function IntakeAnswers({ form }: { form: IntakeFormPreview }) {
  return (
    <div className="p-11">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="type-body-sm font-medium">{form.form_name}</p>
          <p className="text-11 text-ink-muted">
            {form.source} ·{" "}
            {form.submitted_at
              ? new Date(form.submitted_at).toLocaleString()
              : "Date unavailable"}
          </p>
        </div>
        <span className="type-label-xs text-ink-muted">
          {form.answers.length} answers
        </span>
      </div>
      <IntakeAnswerTable answers={form.answers} />
    </div>
  );
}

function PatientFacts({
  data,
  item,
}: {
  data: PatientBriefResponse;
  item: PrescriptionWorkItem;
}) {
  const p = data.profile;
  const { prescriptionMedication, otherActiveMedications: otherMedications } =
    prescriptionMedicationContext(data);
  const facts = [
    [
      "DOB / age",
      [p.date_of_birth, p.age ? `${p.age}y` : null].filter(Boolean).join(" · "),
    ],
    ["Sex", p.sex],
    ["State", p.state],
    ["Phone", p.phone],
    ["Email", p.email],
  ].filter(([, value]) => Boolean(value));
  return (
    <section className="border border-line-subtle">
      <div className="px-10 py-8 bg-sunken border-b border-line-subtle flex items-center gap-6">
        <UserRound size={13} aria-hidden />
        <h3 className="type-label-sm font-medium">Patient chart</h3>
      </div>
      <div className="divide-y divide-line-subtle">
        {facts.map(([label, value]) => (
          <div
            key={label}
            className="px-10 py-7 grid grid-cols-[7rem_minmax(0,1fr)] gap-7"
          >
            <span className="type-label-xs text-ink-muted">{label}</span>
            <span className="type-body-sm break-words">{value}</span>
          </div>
        ))}
      </div>
      <div className="p-10 border-t border-line-subtle flex flex-col gap-7">
        <p className="type-label-xs text-ink-muted">
          MEDICATION FOR THIS PRESCRIPTION
        </p>
        {prescriptionMedication ? (
          <div className="border border-success/30 bg-success-wash p-8">
            <p className="type-body-sm font-medium">
              {prescriptionMedication.name}
            </p>
            <p className="text-11 text-ink-muted mt-2">
              {[
                prescriptionMedication.dose,
                prescriptionMedication.frequency,
                prescriptionMedication.route,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {prescriptionMedication.instructions && (
              <p className="type-body-sm mt-4">
                {prescriptionMedication.instructions}
              </p>
            )}
          </div>
        ) : (
          <div className="border border-line-subtle bg-sunken p-8">
            <p className="type-body-sm font-medium">
              {item.medication || item.product}
            </p>
            <p className="text-11 text-ink-muted mt-2">
              No exact active chart-medication match is on file for this
              prescription yet.
            </p>
          </div>
        )}

        {otherMedications.length > 0 && (
          <details className="border-t border-line-subtle pt-7">
            <summary className="cursor-pointer type-label-xs text-ink-muted">
              OTHER ACTIVE CHART MEDICATIONS · {otherMedications.length}
            </summary>
            <div className="flex flex-col gap-5 mt-7">
              {otherMedications.map((medication) => (
                <div key={medication.id} className="type-body-sm">
                  <span className="font-medium">{medication.name}</span>
                  {(medication.instructions || medication.frequency) && (
                    <span className="text-ink-muted">
                      {" "}
                      · {medication.instructions || medication.frequency}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}

function AppointmentCard({
  data,
  patientId,
}: {
  data: PatientBriefResponse;
  patientId: string;
}) {
  const join = useJoinClinicianAppointment();
  return (
    <section className="border border-line-subtle">
      <div className="px-10 py-8 bg-sunken border-b border-line-subtle flex items-center gap-6">
        <CalendarClock size={13} aria-hidden />
        <h3 className="type-label-sm font-medium">Appointments</h3>
      </div>
      {data.appointments.length ? (
        <div className="divide-y divide-line-subtle">
          {data.appointments.map((appointment) => {
            const start = new Date(appointment.starts_at);
            return (
              <div
                key={appointment.id}
                className="p-9 flex items-center justify-between gap-8"
              >
                <div className="min-w-0">
                  <p className="type-body-sm font-medium">
                    {start.toLocaleString([], {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </p>
                  <p className="text-11 text-ink-muted truncate">
                    {appointment.appointment_type || "Clinical visit"}
                  </p>
                </div>
                {appointment.join_url && (
                  <Button asChild size="sm">
                    <a
                      href={appointment.join_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        join.mutate({
                          appointmentId: appointment.id,
                          patientId,
                        })
                      }
                    >
                      <Video size={12} /> Join
                    </a>
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="p-10 type-body-sm text-ink-muted">
          No upcoming appointment.
        </p>
      )}
    </section>
  );
}

function RecentMessages({
  item,
  data,
}: {
  item: PrescriptionWorkItem;
  data: PatientBriefResponse;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [guideId, setGuideId] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const send = useSendPatientMessage(item.patient.id);
  const draft = usePatientConversationDraft(
    item.patient.id,
    data.recent_conversation_id,
    { enabled: open },
  );
  const draftOutcome = useDraftOutcome();
  const [draftUsed, setDraftUsed] = useState(false);
  const [draftAutoLoaded, setDraftAutoLoaded] = useState(false);
  const templates = useClinicianTemplates("message");
  const guides = useClinicianGuides();
  const preview = data.recent_messages.slice(-3);
  const orderedGuides = [...(guides.data ?? [])]
    .filter((guide) => guide.coa_scope !== "raw_material")
    .sort((left, right) => {
      const leftRelevant = left.product_ids.includes(item.product_id ?? "");
      const rightRelevant = right.product_ids.includes(item.product_id ?? "");
      if (leftRelevant !== rightRelevant) return leftRelevant ? -1 : 1;
      return left.title.localeCompare(right.title);
    });
  const orderedPatientGuides = orderedGuides.filter(
    (guide) => guide.document_kind === "guide",
  );
  const orderedCoas = orderedGuides.filter(
    (guide) =>
      guide.document_kind === "coa" &&
      guide.product_ids.includes(item.product_id ?? ""),
  );

  useEffect(() => {
    setText("");
    setGuideId("");
    setStatusMessage(null);
    setDraftUsed(false);
    setDraftAutoLoaded(false);
  }, [data.recent_conversation_id, item.patient.id]);

  useEffect(() => {
    if (!open || draftAutoLoaded || !draft.data?.content || text.trim()) return;
    setText(draft.data.content);
    setDraftUsed(true);
    setDraftAutoLoaded(true);
  }, [draft.data?.content, draftAutoLoaded, open, text]);

  const submit = async () => {
    if (!text.trim() && !guideId) return;
    setStatusMessage(null);
    try {
      await send.mutateAsync({
        content: text.trim(),
        conversation_id: data.recent_conversation_id,
        guide_id: guideId || undefined,
      });
      if (draftUsed && draft.data?.id) {
        draftOutcome.mutate({
          draftId: draft.data.id,
          outcome:
            text.trim() === draft.data.content.trim() ? "accepted" : "edited",
          finalText: text.trim(),
        });
      }
      setText("");
      setGuideId("");
      setStatusMessage("Message sent in Healthie.");
    } catch (error) {
      setStatusMessage(clinicianMessageError(error));
    }
  };

  return (
    <>
      <section className="border border-line-subtle">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full px-10 py-8 bg-sunken border-b border-line-subtle flex items-center justify-between gap-6 text-left hover:bg-line-subtle/50 focus-visible:outline-none focus-visible:shadow-focus"
        >
          <span className="flex items-center gap-6">
            <MessageSquare size={13} aria-hidden />
            <span className="type-label-sm font-medium">
              Recent Healthie chat
            </span>
          </span>
          <span className="type-label-xs text-ink-muted flex items-center gap-4">
            View & message <ChevronRight size={12} aria-hidden />
          </span>
        </button>
        {preview.length ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full p-9 flex flex-col gap-6 text-left hover:bg-sunken/50 focus-visible:outline-none focus-visible:shadow-focus"
          >
            {preview.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full p-10 type-body-sm text-ink-muted text-left hover:bg-sunken/50"
          >
            No recent messages. Start a Healthie conversation.
          </button>
        )}
      </section>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={`Healthie chat · ${patientName(item)}`}
        footer={
          <div className="border-t border-line-subtle bg-raised p-10 flex flex-col gap-7">
            {data.recent_conversation_id && (
              <div className="border border-line-subtle bg-accent-wash p-8 flex items-start justify-between gap-8">
                <div className="min-w-0">
                  <p className="type-label-xs font-medium flex items-center gap-5">
                    <Sparkles size={12} aria-hidden /> AI DRAFT REPLY
                  </p>
                  <p className="type-body-sm text-ink-secondary mt-4 line-clamp-2">
                    {draft.isLoading
                      ? "Preparing a chart-grounded reply…"
                      : draft.data?.content
                        ? draft.data.content
                        : draft.isError
                          ? "The draft could not be prepared. You can still reply manually."
                          : "Open this chat to prepare a reply."}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={draft.isLoading}
                  disabled={!draft.data?.content}
                  onClick={() => {
                    if (!draft.data?.content) return;
                    setText(draft.data.content);
                    setDraftUsed(true);
                  }}
                >
                  <Sparkles size={12} /> Use AI draft
                </Button>
              </div>
            )}
            {(templates.data?.length ?? 0) > 0 && (
              <select
                value=""
                onChange={(event) => {
                  const template = templates.data?.find(
                    (entry) => entry.id === event.target.value,
                  );
                  if (template) setText(template.content);
                }}
                className="min-h-34 border border-line bg-raised px-8 type-label-xs"
                aria-label="Use a message template"
              >
                <option value="">Use a message template…</option>
                {templates.data?.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            )}
            <div className="flex flex-wrap items-center gap-8">
              <GuideAttachmentPicker
                guides={[...orderedPatientGuides, ...orderedCoas]}
                value={guideId}
                onChange={setGuideId}
                disabled={send.isPending}
                loading={guides.isLoading}
                failed={guides.isError}
                onRetry={() => void guides.refetch()}
                productId={item.product_id}
              />
              <LearningLinkPicker
                disabled={send.isPending}
                onSelect={(title, url) =>
                  setText((current) => appendLearningLink(current, title, url))
                }
              />
            </div>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={4}
              placeholder="Message the patient…"
              className="w-full border border-line bg-sunken px-10 py-8 type-body-sm outline-none resize-y focus:shadow-focus"
            />
            <div className="flex items-center justify-between gap-8">
              <span
                className={`text-11 ${statusMessage?.startsWith("Message sent") ? "text-success" : "text-danger"}`}
              >
                {statusMessage}
              </span>
              <Button
                type="button"
                size="sm"
                loading={send.isPending}
                disabled={!text.trim() && !guideId}
                onClick={submit}
              >
                <Send size={12} aria-hidden /> Send message
              </Button>
            </div>
            <p className="text-10 text-ink-muted">
              Sends to the patient’s live Healthie conversation. It does not
              change any inbox resolution state. Guide PDFs use secure,
              patient-scoped links.
            </p>
          </div>
        }
      >
        <div className="p-12 flex flex-col gap-7">
          {data.recent_messages.length ? (
            data.recent_messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))
          ) : (
            <p className="type-body-sm text-ink-muted">
              No messages yet. Your first send will start a direct Healthie
              conversation for this patient.
            </p>
          )}
        </div>
      </Drawer>
    </>
  );
}

function MessageBubble({
  message,
}: {
  message: PatientBriefResponse["recent_messages"][number];
}) {
  const patient = message.author_kind === "patient";
  return (
    <div
      className={`max-w-[92%] p-7 type-body-sm ${patient ? "bg-sunken self-start" : "bg-ink text-page self-end"}`}
    >
      {message.text && (
        <RichMessageContent text={message.text} inverse={!patient} />
      )}
      {(message.attachments ?? []).map((attachment, index) =>
        attachment.kind === "image" ? (
          <a
            key={`${attachment.url}-${index}`}
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            className="block mt-5"
          >
            <img
              src={attachment.url}
              alt={attachment.name || "Patient attachment"}
              loading="lazy"
              className="max-w-full max-h-72 object-contain border border-line bg-raised"
            />
          </a>
        ) : attachment.kind === "audio" ? (
          <audio
            key={`${attachment.url}-${index}`}
            src={attachment.url}
            controls
            className="max-w-full mt-5"
          />
        ) : (
          <MessageDocumentCard
            key={`${attachment.url}-${index}`}
            href={attachment.url}
            title={attachment.name || "View document"}
            mime={attachment.mime}
          />
        ),
      )}
      <p
        className={`text-10 mt-3 ${patient ? "text-ink-muted" : "text-page/60"}`}
      >
        {message.author_name || message.author_kind} ·{" "}
        {message.created_at
          ? new Date(message.created_at).toLocaleString()
          : ""}
      </p>
    </div>
  );
}

function PatientDocuments({ data }: { data: PatientBriefResponse }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = data.documents.find(
    (document) => document.id === selectedId,
  );
  return (
    <>
      <section className="border border-line-subtle">
        <div className="px-10 py-8 bg-sunken border-b border-line-subtle flex items-center justify-between gap-6">
          <span className="flex items-center gap-6">
            <FolderOpen size={13} aria-hidden />
            <span className="type-label-sm font-medium">Patient documents</span>
          </span>
          <span className="type-label-xs text-ink-muted">
            {data.documents.length} on file
          </span>
        </div>
        {data.documents.length ? (
          <div className="divide-y divide-line-subtle">
            {data.documents.map((document) => (
              <button
                key={document.id}
                type="button"
                onClick={() => setSelectedId(document.id)}
                className="w-full px-10 py-8 flex items-center justify-between gap-8 text-left hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
              >
                <span className="min-w-0 flex items-center gap-7">
                  <FileImage
                    size={14}
                    className="shrink-0 text-ink-muted"
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="type-body-sm font-medium block truncate">
                      {document.filename}
                    </span>
                    <span className="text-10 text-ink-muted uppercase">
                      {document.purpose} ·{" "}
                      {new Date(document.uploaded_at).toLocaleDateString()}
                    </span>
                  </span>
                </span>
                <Eye
                  size={13}
                  className="shrink-0 text-ink-muted"
                  aria-hidden
                />
              </button>
            ))}
          </div>
        ) : (
          <p className="p-10 type-body-sm text-ink-muted">
            No patient documents on file.
          </p>
        )}
      </section>
      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.filename ?? "Patient document"}
      >
        {selected && <DocumentPreview document={selected} />}
      </Drawer>
    </>
  );
}

export function PrescribingActions({
  item,
  data,
  onClosePatient,
}: {
  item: PrescriptionWorkItem;
  data: PatientBriefResponse;
  onClosePatient: () => void;
}) {
  const { data: session } = useClinicianSession();
  const [flow, setFlow] = useState<PrescriptionDecision | null>(null);
  const [visitOverrideOpen, setVisitOverrideOpen] = useState(false);
  const [visitRequestOpen, setVisitRequestOpen] = useState(false);
  const [withdrawConfirming, setWithdrawConfirming] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const withdrawVisit = useWithdrawClinicianVisit(item.patient.id);
  const [message, setMessage] = useState<string | null>(null);
  const approved = data.clinician_review?.decision === "approve";
  const visitRequest = openVisitRequest(item);
  const visitBlocked =
    item.stage === "sync_required" || item.stage === "visit_scheduled";
  const canDecide = [
    "ready_to_prescribe",
    "approval_required",
    "rejected",
  ].includes(item.stage);

  if (item.submission_followup) {
    return (
      <section className="border border-warning/30 bg-warning-wash p-13 flex gap-8">
        <AlertTriangle size={17} className="text-warning shrink-0" />
        <div>
          <p className="type-label-sm text-warning">Approved · pharmacy follow-up</p>
          <p className="type-body-sm mt-3">
            Your approval and chart medication are saved. Pharmacy fulfillment has not been confirmed.
            Operations will reconcile the existing support ticket; this is not a request to repeat your clinical review.
          </p>
          <p className="text-11 text-ink-muted mt-4">
            A saved chart record may still need prescription validation. If a new prescription or clinical decision
            is required, support will send an explicit clinician handoff.
          </p>
        </div>
      </section>
    );
  }

  if (item.stage === "prescribed") {
    return (
      <section className="border border-success/30 bg-success-wash p-13 flex gap-8">
        <CheckCircle2 size={17} className="text-success shrink-0" />
        <div>
          <p className="type-label-sm text-success">Prescribed</p>
          <p className="type-body-sm mt-3">
            {String(
              data.medication_record?.name ??
                item.medication ??
                "Medication record written",
            )}
          </p>
          {item.decision_workflow && (
            <p className="text-11 text-ink-muted mt-4">
              {item.decision_workflow.patient_message?.status === "sent"
                ? "Patient notified of approval."
                : "Approval message needs attention."}
            </p>
          )}
        </div>
      </section>
    );
  }

  if (item.stage === "missing_info") {
    return (
      <section className="border border-warning/30 bg-warning-wash p-13 flex gap-8">
        <AlertTriangle size={17} className="text-warning shrink-0" />
        <div className="min-w-0">
          <p className="type-label-sm text-warning">Missing information</p>
          <p className="type-body-sm mt-3">
            {item.missing_info?.reason_label || "Support follow-up required"}
          </p>
          {item.missing_info?.details && (
            <p className="type-body-sm text-ink-secondary mt-3 whitespace-pre-wrap">
              {item.missing_info.details}
            </p>
          )}
          {item.missing_info?.intake_redo && (
            <p className="text-11 text-ink-secondary mt-5">
              {item.missing_info.intake_redo.resubmitted_at
                ? `New intake received ${formatRedoDate(item.missing_info.intake_redo.resubmitted_at)}.`
                : item.missing_info.intake_redo.notified_at
                  ? `Patient asked to complete a new intake · sent ${formatRedoDate(item.missing_info.intake_redo.notified_at)}. The hold clears when it comes in.`
                  : "Patient asked to complete a new intake · notice pending."}
            </p>
          )}
          <p className="text-11 text-ink-muted mt-5">
            This remains tracked here and in the support queue until resolved.
          </p>
        </div>
      </section>
    );
  }

  if (session && !session.capabilities.prescribe) {
    return (
      <section className="border border-line-subtle bg-sunken p-13 flex gap-8">
        <Stethoscope size={17} className="text-ink-muted shrink-0" />
        <div>
          <p className="type-label-sm">Prescriber-owned action</p>
          <p className="type-body-sm text-ink-muted mt-3">
            You can review the chart, coordinate care, and message this patient.
            The assigned prescriber must approve or reject and create the
            medication record.
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="border border-line-subtle">
        <div className="p-11 bg-sunken border-b border-line-subtle flex items-center gap-6">
          <FileCheck2 size={14} aria-hidden />
          <div>
            <h3 className="type-label-sm font-medium">
              Physician decision & prescription
            </h3>
            <p className="text-11 text-ink-muted">
              One guided action records the note and completes its clinical
              follow-through.
            </p>
          </div>
        </div>
        <div className="p-11 flex flex-col gap-10">
          {data.clinician_review && (
            <div
              className={`p-9 border ${
                approved
                  ? "border-success/30 bg-success-wash"
                  : "border-danger/30 bg-danger-wash"
              }`}
            >
              <p
                className={`type-label-sm ${
                  approved ? "text-success" : "text-danger"
                }`}
              >
                {approved ? "Approved" : "Rejected"} · locked physician note
              </p>
              {data.clinician_review.note && (
                <p className="type-body-sm mt-4 whitespace-pre-wrap">
                  {data.clinician_review.note}
                </p>
              )}
              {data.clinician_review.cosign && (
                <p className="type-label-xs text-ink-muted mt-6">
                  {data.clinician_review.cosign.status === "cosigned"
                    ? `Cosigned by ${data.clinician_review.cosign.cosigned_by_name || "the supervising physician"}${
                        data.clinician_review.cosign.cosigned_at
                          ? `, ${new Date(data.clinician_review.cosign.cosigned_at).toLocaleDateString()}`
                          : ""
                      }`
                    : `Chart review: waiting for ${data.clinician_review.cosign.cosigner_name || "the supervising physician"} to cosign`}
                </p>
              )}
            </div>
          )}

          {item.stage === "rejected" && item.decision_workflow && (
            <div className="p-9 border border-danger/30 bg-danger-wash">
              <p className="type-label-sm text-danger">
                Rejection follow-through
              </p>
              <div className="mt-4 flex flex-wrap gap-x-12 gap-y-3 type-body-sm">
                <span>
                  {item.decision_workflow.subscription?.cancelled
                    ? "Subscription canceled"
                    : "Order canceled"}
                </span>
                {item.decision_workflow.refund?.amount_cents != null && (
                  <span>
                    $
                    {(item.decision_workflow.refund.amount_cents / 100).toFixed(
                      2,
                    )}{" "}
                    refunded
                  </span>
                )}
                <span>
                  {item.decision_workflow.patient_message?.status === "sent"
                    ? "Patient notified"
                    : "Patient notification needs attention"}
                </span>
              </div>
            </div>
          )}

          {visitRequest && (
            <div
              className="border border-info/30 bg-info-wash p-10 flex flex-col gap-6"
              data-testid="visit-request-banner"
            >
              <div className="flex flex-wrap items-start justify-between gap-8">
                <div className="min-w-0">
                  <p className="type-label-sm text-info">
                    Visit requested · {visitRequest.reason_label}
                  </p>
                  <p className="text-11 text-ink-muted mt-3">
                    Requested {new Date(visitRequest.requested_at).toLocaleString()}
                  </p>
                  {visitRequest.note && (
                    <p className="type-body-sm text-ink-secondary mt-4 whitespace-pre-wrap">
                      {visitRequest.note}
                    </p>
                  )}
                  <p className="type-body-sm mt-4">
                    Prescribing unlocks when the visit starts, or record
                    attendance with Patient attended.
                  </p>
                </div>
                {withdrawConfirming ? (
                  <div className="flex flex-wrap items-center gap-6">
                    <span className="text-11 text-ink-muted">
                      Withdraw and return to the prior gate?
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={withdrawVisit.isPending}
                      onClick={() => setWithdrawConfirming(false)}
                    >
                      Keep request
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      loading={withdrawVisit.isPending}
                      onClick={async () => {
                        setWithdrawError(null);
                        try {
                          await withdrawVisit.mutateAsync({ order_id: item.id });
                          setWithdrawConfirming(false);
                          setMessage("Visit request withdrawn.");
                        } catch (failure) {
                          setWithdrawError(
                            failure instanceof Error
                              ? failure.message
                              : "The request could not be withdrawn.",
                          );
                        }
                      }}
                    >
                      Confirm withdraw
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setMessage(null);
                      setWithdrawConfirming(true);
                    }}
                  >
                    Withdraw request
                  </Button>
                )}
              </div>
              {withdrawError && (
                <p className="type-body-sm text-danger">{withdrawError}</p>
              )}
            </div>
          )}

          {!data.physician_note_enabled ? (
            <p className="type-body-sm text-warning">
              Physician-note writing is not configured in this environment.
            </p>
          ) : visitBlocked ? (
            <div className="border border-warning/30 bg-warning-wash p-10 flex flex-wrap items-center justify-between gap-8">
              <p className="type-body-sm text-warning">
                {stageFor(item).detail}
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setMessage(null);
                  setVisitOverrideOpen(true);
                }}
              >
                Override visit requirement
              </Button>
            </div>
          ) : approved && item.stage === "ready_to_prescribe" ? (
            <div className="flex flex-wrap items-center justify-between gap-8">
              <p className="type-body-sm text-ink-muted">
                {data.medication_record
                  ? "The chart prescription is saved. Pharmacy submission still needs to complete."
                  : "The note is locked. Complete the medication record to finish this prescription."}
              </p>
              <Button
                type="button"
                size="sm"
                className="bg-success text-white border-success hover:bg-success/90"
                onClick={() => {
                  setMessage(null);
                  setFlow("approve");
                }}
              >
                {data.medication_record
                  ? "Retry pharmacy submission"
                  : "Complete prescription"}
              </Button>
            </div>
          ) : !data.clinician_review && canDecide ? (
            <div className="flex flex-wrap gap-7">
              <Button
                type="button"
                size="sm"
                className="bg-success text-white border-success hover:bg-success/90"
                onClick={() => {
                  setMessage(null);
                  setFlow("approve");
                }}
              >
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-danger text-white border-danger hover:bg-danger/90"
                onClick={() => {
                  setMessage(null);
                  setFlow("reject");
                }}
              >
                Reject
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-warning text-ink border-warning hover:bg-warning/85"
                onClick={() => {
                  setMessage(null);
                  setFlow("missing_info");
                }}
              >
                Missing info
              </Button>
              {!visitRequest && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setMessage(null);
                    setVisitRequestOpen(true);
                  }}
                >
                  Request visit
                </Button>
              )}
            </div>
          ) : !data.clinician_review ? (
            <div className="border border-warning/30 bg-warning-wash p-10 type-body-sm text-warning">
              {stageFor(item).detail}
            </div>
          ) : null}

          {message && <p className="type-body-sm text-success">{message}</p>}
        </div>
      </section>

      {flow && (
        <PrescriptionDecisionModal
          item={item}
          data={data}
          initialDecision={flow}
          onClose={() => setFlow(null)}
          onComplete={(nextMessage, completedDecision) => {
            setFlow(null);
            setMessage(nextMessage);
            if (completedDecision === "missing_info") {
              onClosePatient();
            }
          }}
        />
      )}

      {visitRequestOpen && (
        <VisitRequestModal
          item={item}
          onClose={() => setVisitRequestOpen(false)}
          onComplete={(patientNotified) => {
            setVisitRequestOpen(false);
            setMessage(
              patientNotified
                ? "Visit requested. The patient has been asked to book. Patient notified by email/SMS."
                : "Visit requested. The patient has been asked to book.",
            );
          }}
        />
      )}

      {visitOverrideOpen && (
        <VisitOverrideModal
          item={item}
          data={data}
          onClose={() => setVisitOverrideOpen(false)}
          onComplete={() => {
            setVisitOverrideOpen(false);
            setFlow("approve");
          }}
        />
      )}
    </>
  );
}
import { PrescriptionSupply } from "./PrescriptionSupply";
